/**
 * 最小限のMarkdown描画。
 *
 * 外部Libraryを増やさず、成果物Previewに必要な範囲だけ扱う。
 * 入力は必ず先にescapeし、生成するのはこのファイルが作る限定タグのみとする。
 * 生HTMLの通し込みは行わない。
 */

function escapeHtml(value: string): string {
	return value
		.replaceAll("&", "&amp;")
		.replaceAll("<", "&lt;")
		.replaceAll(">", "&gt;")
		.replaceAll('"', "&quot;")
		.replaceAll("'", "&#39;");
}

function inline(text: string): string {
	return escapeHtml(text)
		.replace(/`([^`]+)`/g, "<code>$1</code>")
		.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
		.replace(/(^|[^*])\*([^*]+)\*/g, "$1<em>$2</em>");
}

export function renderMarkdown(source: string): string {
	const lines = source.replace(/\r\n/g, "\n").split("\n");
	const out: string[] = [];
	let listType: "ul" | "ol" | null = null;
	let tableRows: string[][] = [];

	const closeList = () => {
		if (listType) {
			out.push(`</${listType}>`);
			listType = null;
		}
	};

	const flushTable = () => {
		if (tableRows.length === 0) return;
		const [header, ...body] = tableRows;
		// 2行目が区切り行（---）なら除外する
		const rows = body.filter((row) => !row.every((cell) => /^:?-{2,}:?$/.test(cell.trim())));
		out.push("<table><thead><tr>");
		for (const cell of header ?? []) out.push(`<th>${inline(cell.trim())}</th>`);
		out.push("</tr></thead><tbody>");
		for (const row of rows) {
			out.push("<tr>");
			for (const cell of row) out.push(`<td>${inline(cell.trim())}</td>`);
			out.push("</tr>");
		}
		out.push("</tbody></table>");
		tableRows = [];
	};

	for (const raw of lines) {
		const line = raw.trimEnd();

		if (line.includes("|") && line.trim().startsWith("|")) {
			closeList();
			tableRows.push(
				line
					.trim()
					.replace(/^\||\|$/g, "")
					.split("|"),
			);
			continue;
		}
		flushTable();

		const heading = /^(#{1,4})\s+(.*)$/.exec(line);
		if (heading) {
			closeList();
			const level = Math.min(heading[1]?.length ?? 1, 4);
			out.push(`<h${level}>${inline(heading[2] ?? "")}</h${level}>`);
			continue;
		}

		const ordered = /^\s*\d+\.\s+(.*)$/.exec(line);
		if (ordered) {
			if (listType !== "ol") {
				closeList();
				out.push("<ol>");
				listType = "ol";
			}
			out.push(`<li>${inline(ordered[1] ?? "")}</li>`);
			continue;
		}

		const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
		if (bullet) {
			if (listType !== "ul") {
				closeList();
				out.push("<ul>");
				listType = "ul";
			}
			out.push(`<li>${inline(bullet[1] ?? "")}</li>`);
			continue;
		}

		if (line.trim() === "") {
			closeList();
			continue;
		}

		closeList();
		out.push(`<p>${inline(line)}</p>`);
	}

	closeList();
	flushTable();
	return out.join("");
}
